import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { fetchData } from '../../../helpers/fetchData'
import { sendClaim, matchClaimInsurancePlan } from '../../../services/InvoicesService'
import { IoIosSend } from 'react-icons/io'
import CustomButton from '../../../shared/CustomButton'
import CustomPagination from '../../../shared/CustomPagination'
import CustomTable from '../../../shared/CustomTable'
import DropdownField from '../../../shared/DropdownField'
import MultiSelectField from '../../../shared/MultiSelectField'
import TextInput from '../../../shared/TextInput'
import CustomChip from '../../../shared/CustomChip'
import { EyeIcon, EditIcon } from '../../../utils/TableActionIcons'
import { RunStepIcon, JobProgressIcon } from '../../../utils/ClaimsActionIcons'
import { MdNoteAdd } from 'react-icons/md'
import { navigateSecurely } from '../../../utils/secureNavigation'
import { setSelectPatientData } from '../../../redux/slices/BeneficiarySlice'
import { setSelectedVisitData } from '../../../redux/slices/VisitsSlice'
import { getTabState } from '../../../hooks/useTabPersistence'
import JobProgressModal from './JobProgressModal'
import RemarkModal from './RemarkModal'
import ReusableModal from '../../../shared/ReuseableModal'
import ConfirmationModal from '../../../shared/ConfirmationModal'
import BulkSfdaAutoCorrectionModal from './BulkSfdaAutoCorrectionModal'
import ICDBulkReplaceModal from '../../reports/ICDBulkReplaceModal'
import {
  MdAutoFixHigh,
  MdBuild,
  MdDescription,
  MdFactCheck,
  MdHandshake,
  MdOutlineFactCheck,
  MdOutlineMoveUp,
  MdOutlinePlayArrow,
  MdRefresh,
  MdSend,
  MdVerified,
  MdWarning
} from 'react-icons/md'
import { FaExchangeAlt } from 'react-icons/fa'

const persistedFilterModes = new Set(['audit', 'auto-fix', 'acknowledge', 'generate', 'generated-audit', 'generated-auto-fix', 'clearance', 'submit'])

const readPersistedStepFilters = mode => {
  if (!persistedFilterModes.has(mode)) return {}

  try {
    return JSON.parse(localStorage.getItem(`claimBatchStepFilters:${mode}`) || '{}')
  } catch (error) {
    console.warn('Failed to read saved claim batch filters:', error)
    return {}
  }
}

const STEP_RETURN_SNAPSHOT_PREFIX = 'claimBatchStepReturnSnapshot'
const STEP_RETURN_SNAPSHOT_TTL_MS = 10 * 60 * 1000 // 10 minutes

const saveStepReturnSnapshot = (mode, snapshot) => {
  try {
    sessionStorage.setItem(
      `${STEP_RETURN_SNAPSHOT_PREFIX}:${mode}`,
      JSON.stringify({ ...snapshot, savedAt: Date.now() })
    )
  } catch (error) {
    console.warn('Failed to save claim batch return snapshot:', error)
  }
}

const readStepReturnSnapshot = mode => {
  try {
    const snapshot = JSON.parse(
      sessionStorage.getItem(`${STEP_RETURN_SNAPSHOT_PREFIX}:${mode}`) || 'null'
    )
    if (!snapshot) return null
    if (Date.now() - (snapshot.savedAt || 0) > STEP_RETURN_SNAPSHOT_TTL_MS) {
      sessionStorage.removeItem(`${STEP_RETURN_SNAPSHOT_PREFIX}:${mode}`)
      return null
    }
    return snapshot
  } catch (error) {
    console.warn('Failed to read claim batch return snapshot:', error)
    return null
  }
}

const clearStepReturnSnapshot = mode => {
  try {
    sessionStorage.removeItem(`${STEP_RETURN_SNAPSHOT_PREFIX}:${mode}`)
  } catch (error) {
    console.warn('Failed to clear claim batch return snapshot:', error)
  }
}

const stepTitles = {
  audit: 'Run Audit',
  'auto-fix': 'Auto Correct',
  acknowledge: 'Acknowledge',
  generate: 'Generate Claim',
  'generated-audit': 'Audit Generated Claims',
  'generated-auto-fix': 'Auto Correct Generated Claims',
  clearance: 'Clearance',
  submit: 'Submit Claim'
}

const jobStatusChipType = status => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
    case 'resolved':
      return 'success'
    case 'completed_with_errors':
    case 'processing':
    case 'queued':
      return 'warning'
    case 'failed':
      return 'danger'
    default:
      return 'secondary'
  }
}

const endpoints = {
  audit: 'audit',
  'auto-fix': 'auto-fix',
  acknowledge: 'acknowledge',
  generate: 'generate',
  'generated-audit': 'generated-audit',
  'generated-auto-fix': 'generated-auto-fix',
  clearance: 'clearance',
  submit: 'submit'
}

const stepIcons = {
  audit: MdFactCheck,
  'auto-fix': MdAutoFixHigh,
  acknowledge: MdHandshake,
  generate: MdDescription,
  'generated-audit': MdFactCheck,
  'generated-auto-fix': MdAutoFixHigh,
  clearance: MdVerified,
  submit: MdSend
}

const stepConfirmLabels = {
  audit: 'Run Audit',
  'auto-fix': 'Auto Correct',
  acknowledge: 'Acknowledge',
  generate: 'Generate Claim',
  'generated-audit': 'Run Generated Claim Audit',
  'generated-auto-fix': 'Auto Correct Generated Claims',
  clearance: 'Clearance',
  submit: 'Submit Claim'
}

const stepConfirmIcons = {
  audit: MdOutlinePlayArrow,
  'auto-fix': MdAutoFixHigh,
  acknowledge: MdOutlineFactCheck,
  generate: MdOutlinePlayArrow,
  'generated-audit': MdOutlinePlayArrow,
  'generated-auto-fix': MdAutoFixHigh,
  clearance: MdVerified,
  submit: MdSend
}

const needsRemarks = mode => mode === 'acknowledge' || mode === 'clearance'

const workflowSteps = {
  audit: 'audit',
  'auto-fix': 'auto_fix',
  acknowledge: 'acknowledgment',
  generate: 'generate_claim',
  'generated-audit': 'generated_claim_audit',
  'generated-auto-fix': 'generated_claim_auto_fix',
  clearance: 'clearance',
  submit: 'submit_claim'
}

const getBatchDisplayFinancials = row => {
  const generated = row?.generated_financials
  if (generated && Object.keys(generated).length) return generated

  const initialized = row?.initialization_financials
  if (initialized && Object.keys(initialized).length) {
    const patientShare = Number(initialized.patient_share || 0)
    const payerShare = Number(initialized.payer_share || 0)
    return {
      company_share: payerShare,
      patient_share: patientShare,
      discount: Number(initialized.discount || 0),
      vat:
        Number(initialized.patient_share_with_vat || 0) - patientShare +
        (Number(initialized.payer_share_with_vat || 0) - payerShare)
    }
  }

  const itemFinancials = row?.financials || {}
  return {
    company_share: itemFinancials.company_share || 0,
    patient_share: itemFinancials.patient_share || 0,
    discount: itemFinancials.discount || 0,
    vat: itemFinancials.total_vat || 0
  }
}
const formatBatchAmount = value => {
  const amount = Number(value || 0)
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const formatIssueValue = value => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'number') return formatBatchAmount(value)
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
function ClaimBatchStepList({ mode, onView, onRunBatch, runningBatchId, onShowJob, onMoveStep, workflowListMonth, refreshKey = 0, extraActions = null }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('claimBatchStepListFilters:' + mode) || '{}')
      return {
        page: Number(saved?.page) || 1,
        per_page: Number(saved?.per_page) || 10,
        total_count: 0,
        total_pages: 0
      }
    } catch (error) {
      return { page: 1, per_page: 10, total_count: 0, total_pages: 0 }
    }
  })
  const [listFilters, setListFilters] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('claimBatchStepListFilters:' + mode) || '{}')
      return {
        month: saved?.month || new Date().toISOString().slice(0, 7),
        search: saved?.search || ''
      }
    } catch (error) {
      return { month: new Date().toISOString().slice(0, 7), search: '' }
    }
  })

  const monthOptions = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' })
    return Array.from({ length: 24 }, (_, index) => {
      const date = new Date()
      date.setDate(1)
      date.setMonth(date.getMonth() - index)
      return {
        value: String(date.getFullYear()) + '-' + String(date.getMonth() + 1).padStart(2, '0'),
        label: formatter.format(date)
      }
    })
  }, [])

  const fetchStepBatches = async (page = 1, perPage = pagination.per_page, activeFilters = listFilters) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        step: workflowSteps[mode]
      })
      if (activeFilters.month) params.set('month', activeFilters.month)
      if (activeFilters.search.trim()) params.set('search', activeFilters.search.trim())
      params.set('include_financials', 'true')

      const response = await fetchData('GET', 'v3/claims/batches?' + params.toString(), {})
      setRows(response?.data || [])
      setPagination({
        page: response?.page || page,
        per_page: response?.per_page || perPage,
        total_count: response?.total_count || 0,
        total_pages: response?.total_pages || 0
      })
    } catch (error) {
      console.error('Error fetching workflow batches', error)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStepBatches(pagination.page, pagination.per_page)
  }, [refreshKey])

  useEffect(() => {
    if (!workflowListMonth) return
    const nextFilters = { ...listFilters, month: workflowListMonth }
    setListFilters(nextFilters)
    fetchStepBatches(1, pagination.per_page, nextFilters)
  }, [workflowListMonth])
  useEffect(() => {
    localStorage.setItem(
      'claimBatchStepListFilters:' + mode,
      JSON.stringify({
        ...listFilters,
        page: pagination.page,
        per_page: pagination.per_page
      })
    )
  }, [mode, listFilters, pagination.page, pagination.per_page])

  const columns = [
    {
      header: t('Actions'),
      body: row => {
        const isThisRowRunning = runningBatchId === row?.batch_id
        const isAnyRunning = !!runningBatchId
        const isJobBusy = ['queued', 'processing'].includes(
          String(row?.current_job?.status || '').toLowerCase()
        )
        return (
          <div className='flex items-center gap-1.5'>
            <EyeIcon
              iconSize='17'
              title={t('View Batch Details')}
              disabled={isAnyRunning}
              onClick={() => !isAnyRunning && onView(row, listFilters.month)}
            />
            <RunStepIcon
              title={t(stepTitles[mode])}
              loading={isThisRowRunning}
              disabled={isAnyRunning || isJobBusy}
              onClick={() => !isAnyRunning && !isJobBusy && onRunBatch?.(row)}
            />
            <JobProgressIcon
              title={t('Current Job Progress')}
              disabled={!row?.current_job?.job_id}
              onClick={() => onShowJob?.(row)}
            />
            {!!onMoveStep && (
              <MdOutlineMoveUp
                size={17}
                title={t('Move Step')}
                className={
                  isAnyRunning
                    ? 'cursor-not-allowed opacity-40 text-amber-500'
                    : 'cursor-pointer text-amber-500 hover:text-amber-700'
                }
                onClick={() => !isAnyRunning && onMoveStep(row)}
              />
            )}
          </div>
        )
      }
    },
    {
      header: t('Batch No'),
      body: row => (
        <div>
          <div>{row?.batch_number || '-'}</div>
          <div className='text-xs text-gray-500 break-words'>
            {row?.tpa_name || t('TPA not configured')} - {row?.company_count ?? row?.payers?.length ?? 0} {t('companies')}
          </div>
        </div>
      ),
      bodyCellClass: '!whitespace-normal !min-w-[220px]'
    },
    {
      header: t('Generated Amount'),
      body: row => {
        const financials = getBatchDisplayFinancials(row)
        return formatBatchAmount(Number(financials.company_share || 0) + Number(financials.patient_share || 0))
      }
    },
    { header: t('Company Share'), body: row => formatBatchAmount(getBatchDisplayFinancials(row).company_share) },
    { header: t('Patient Share'), body: row => formatBatchAmount(getBatchDisplayFinancials(row).patient_share) },
    { header: t('Discount'), body: row => formatBatchAmount(getBatchDisplayFinancials(row).discount) },
    { header: t('VAT'), body: row => formatBatchAmount(getBatchDisplayFinancials(row).vat) },
    { header: t('Visits'), body: row => row?.visit_count || 0 },
    { header: t('Current Step'), body: row => formatWorkflowLabel(row?.current_step) || '-' },
    { header: t('Status'), body: row => formatWorkflowLabel(row?.current_status) || '-' }
  ]

  const resetFilters = () => {
    const nextFilters = { month: new Date().toISOString().slice(0, 7), search: '' }
    setListFilters(nextFilters)
    fetchStepBatches(1, pagination.per_page, nextFilters)
  }

  return (
    <div>
      <div className='mb-2 flex flex-wrap items-center gap-2'>
        <div className='w-[220px]'>
          <DropdownField
            useFormik={false}
            value={listFilters.month}
            handleChange={event => setListFilters(prev => ({ ...prev, month: event.target.value || '' }))}
            options={monthOptions}
            placeholder={t('Select Month')}
            filter
          />
        </div>
        <div className='w-[260px]'>
          <TextInput
            useFormik={false}
            name='batch_number'
            value={listFilters.search}
            handleChange={event => setListFilters(prev => ({ ...prev, search: event.target.value }))}
            placeholder={t('Search Batch No')}
          />
        </div>
        <div className='flex gap-2'>
          <CustomButton
            title={t('ApplyFilters')}
            buttonType='primary'
            icon='pi pi-filter'
            className='w-fit h-[35px]'
            disabled={loading}
            onClick={() => fetchStepBatches(1, pagination.per_page)}
          />
          {extraActions}
          <CustomButton
            title={t('Reset')}
            buttonType='reset'
            icon='pi pi-sync'
            className='w-fit h-[35px]'
            disabled={loading}
            onClick={resetFilters}
          />
        </div>
      </div>
      <CustomTable data={rows} columns={columns} isLoading={loading} emptyMessage={t('No records found')} />
      {!!pagination.total_count && (
        <CustomPagination
          currentPage={pagination.page}
          rowsPerPage={pagination.per_page}
          totalRecords={pagination.total_count}
          totalPages={pagination.total_pages || 1}
          onPageChange={(page, perPage) => fetchStepBatches(page, perPage)}
        />
      )}
    </div>
  )
}
const formatWorkflowLabel = value =>
  String(value || '')
    .split(/[_-]+/)
    .filter(Boolean)
    .map(word => {
      const normalized = word.toLowerCase()
      if (normalized === 'emr' || normalized === 'icd' || normalized === 'sfda') return normalized.toUpperCase()
      return normalized.charAt(0).toUpperCase() + normalized.slice(1)
    })
    .join(' ')

function ClaimBatchStepTab({ mode, selectedBatchId, setSelectedBatchId, workflowListMonth }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const persistedFilters = useMemo(() => readPersistedStepFilters(mode), [mode])
  const shouldPersistFilters = persistedFilterModes.has(mode)
  const [showDetails, setShowDetails] = useState(false)
  useEffect(() => {
    if (!selectedBatchId) setShowDetails(false)
  }, [selectedBatchId])
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [batches, setBatches] = useState([])
  const [selectedMonth, setSelectedMonth] = useState(() => persistedFilters?.month || '')
  const [batch, setBatch] = useState(null)
  const [rows, setRows] = useState([])
  const [issueSummary, setIssueSummary] = useState([])
  const [jobBatch, setJobBatch] = useState(null)
  const [actionModalVisible, setActionModalVisible] = useState(false)
  const [confirmBatchRow, setConfirmBatchRow] = useState(null)
  const [autoCorrectLoading, setAutoCorrectLoading] = useState(false)
  const [actionModalBatchId, setActionModalBatchId] = useState('')
  const [overrideUnresolved, setOverrideUnresolved] = useState(false)
  const [runningBatchId, setRunningBatchId] = useState('')
  const [listRefreshKey, setListRefreshKey] = useState(0)
  const [autoFixIssueTypes, setAutoFixIssueTypes] = useState(() => persistedFilters?.auto_fix_issue_types || (persistedFilters?.auto_fix_issue_type ? [persistedFilters.auto_fix_issue_type] : []))
  const [showSfdaModal, setShowSfdaModal] = useState(false)
  const [showIcdModal, setShowIcdModal] = useState(false)
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [showAuditDetailModal, setShowAuditDetailModal] = useState(false)
  const [showReauditConfirm, setShowReauditConfirm] = useState(false)
  const [reauditVisitId, setReauditVisitId] = useState(null)
  const [reauditLoading, setReauditLoading] = useState(false)
  const [showAutoCorrectConfirm, setShowAutoCorrectConfirm] = useState(false)
  const [autoCorrectTarget, setAutoCorrectTarget] = useState(null)
  const [correctingVisitId, setCorrectingVisitId] = useState(null)
  const [resendClaimTarget, setResendClaimTarget] = useState(null)
  const [resendingClaim, setResendingClaim] = useState(false)
  // Move Step state (acknowledge / clearance only)
  const [moveBatch, setMoveBatch] = useState(null)
  const [moveForm, setMoveForm] = useState({ target_step: '', remarks: '' })
  const [movingStep, setMovingStep] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total_count: 0,
    total_pages: 0
  })
  const [filters, setFilters] = useState(() => ({
    batch_id: persistedFilters?.batch_id || selectedBatchId || '',
    issue_type: persistedFilters?.issue_type || '',
    issue_status: persistedFilters?.issue_status || '',
    file_number: persistedFilters?.file_number || '',
    document_id: persistedFilters?.document_id || '',
    patient_name: persistedFilters?.patient_name || ''
  }))
  const monthOptions = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' })
    return Array.from({ length: 24 }, (_, index) => {
      const date = new Date()
      date.setDate(1)
      date.setMonth(date.getMonth() - index)
      return {
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: formatter.format(date)
      }
    })
  }, [])
  const isGeneratedAuditMode = mode === 'generated-audit'
  const isGeneratedAutoFixMode = mode === 'generated-auto-fix'
  const showsGeneratedIssues = isGeneratedAuditMode || isGeneratedAutoFixMode
  const isAutoFixMode = mode === 'auto-fix' || isGeneratedAutoFixMode
  const showsClaims = mode === 'clearance' || mode === 'submit'
  const canMoveStep = mode === 'acknowledge' || mode === 'clearance'

  const batchStepOrder = {
    audit: 1,
    auto_fix: 2,
    acknowledgment: 3,
    generate_claim: 4,
    generated_claim_audit: 5,
    generated_claim_auto_fix: 6,
    clearance: 7,
    submit_claim: 8
  }

  const moveStepOptions = useMemo(() => {
    const currentStep = moveBatch?.current_step || batch?.current_step
    const currentOrder = batchStepOrder[currentStep] || 0
    return [
      { value: 'audit', label: t('Audit') },
      { value: 'auto_fix', label: t('Auto Correct') },
      { value: 'acknowledgment', label: t('Acknowledge') },
      { value: 'generate_claim', label: t('Generate Claim') },
      { value: 'generated_claim_audit', label: t('Generated Claim Audit') },
      { value: 'generated_claim_auto_fix', label: t('Generated Claim Auto Correct') },
      { value: 'clearance', label: t('Clearance') },
      { value: 'submit_claim', label: t('Submit Claim') }
    ].filter(opt => batchStepOrder[opt.value] < currentOrder)
  }, [moveBatch, batch, t])

  const openMoveStepModal = (batchRow) => {
    setMoveBatch(batchRow || batch)
    setMoveForm({ target_step: '', remarks: '' })
  }

  const batchOptions = useMemo(
    () =>
      batches.map(item => ({
        label: `${item?.batch_number || '-'} - ${formatWorkflowLabel(item?.current_status) || '-'}`,
        value: item?.batch_id
      })),
    [batches]
  )

  const getBatchNumber = batchId => {
    if (!batchId) return ''
    if (batch?.batch_id === batchId && batch?.batch_number) return batch.batch_number
    const found = batches.find(item => item?.batch_id === batchId)
    return found?.batch_number || ''
  }

  const autoFixIssueTypeOptions = useMemo(() => {
    const excluded = new Set([
      'icd_inactive',
      'icd inactive',
      'lab_result_not_approved',
      'lab result not approved',
      'radiology_result_not_approved',
      'radiology result not approved',
      'not_approved_lab_result',
      'not_approved_radiology_result'
    ])
    return (issueSummary || [])
      .map(item => {
        const rawKey = String(item?.issue_type || '').toLowerCase()
        const display = formatWorkflowLabel(item.issue_type)
        const displayLower = String(display || '').toLowerCase()
        const excludedHit =
          excluded.has(rawKey) ||
          excluded.has(displayLower) ||
          displayLower.includes('icd inactive') ||
          displayLower.includes('lab result not approved') ||
          displayLower.includes('radiology result not approved')
        return { item, display, excludedHit }
      })
      .filter(entry => !entry.excludedHit)
      .map(({ item, display }) => ({
        label: `${display} (${item?.claims_count || item?.issue_count || 0})`,
        value: item.issue_type
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [issueSummary])
  const fetchBatches = async month => {
    if (!month) {
      setBatches([])
      return
    }

    setLoading(true)
    try {
      const response = await fetchData(
        'GET',
        `v3/claims/batches?page=1&per_page=100&month=${encodeURIComponent(month)}`,
        {}
      )
      setBatches(response?.data || [])
    } catch (error) {
      console.error('Error fetching batches', error)
      setBatches([])
    } finally {
      setLoading(false)
    }
  }

  const handleMonthChange = async event => {
    const month = event.target.value || ''
    setSelectedMonth(month)
    setSelectedBatchId('')
    setFilters(prev => ({
      ...prev,
      batch_id: '',
      issue_type: '',
      issue_status: '',
      file_number: '',
      document_id: '',
      patient_name: ''
    }))
    setBatch(null)
    setRows([])
    setIssueSummary([])
    await fetchBatches(month)
  }
  const buildVisitsQuery = (batchId, page, perPage, filterState = filters) => {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(perPage)
    })

    if (filterState?.issue_type) {
      params.set('issue_type', filterState.issue_type)
    }
    if (mode === 'generate') {
      params.set('include_claim_generation_status', 'true')
    }
    if (mode === 'audit' || mode === 'auto-fix' || mode === 'acknowledge') {
      params.set('only_with_issues', 'true')
    }
    if (filterState?.file_number) {
      params.set('file_number', filterState.file_number)
    }
    if (filterState?.document_id) {
      params.set('document_id', filterState.document_id)
    }
    if (filterState?.patient_name) {
      params.set('patient_name', filterState.patient_name)
    }

    return `v3/claims/batches/${batchId}/visits?${params.toString()}`
  }

  const buildClaimsQuery = (batchId, page, perPage, filterState = filters) => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
    if (filterState?.file_number) params.set('file_number', filterState.file_number)
    if (filterState?.document_id) params.set('document_id', filterState.document_id)
    if (filterState?.patient_name) params.set('patient_name', filterState.patient_name)
    return `v3/claims/batches/${batchId}/claims?${params.toString()}`
  }

  const buildGeneratedIssuesQuery = (batchId, page, perPage, filterState = filters) => {
    const params = new URLSearchParams({ page: String(page), limit: String(perPage) })
    if (filterState?.issue_type) params.set('issue_type', filterState.issue_type)
    if (filterState?.issue_status) params.set('status', filterState.issue_status)
    return `v3/claims/batches/${batchId}/generated-issues?${params.toString()}`
  }

  const fetchBatchDetails = async (
    batchId,
    page = pagination.page,
    perPage = pagination.per_page,
    filterState = filters
  ) => {
    if (!batchId) {
      setBatch(null)
      setRows([])
      setIssueSummary([])
      setPagination(prev => ({ ...prev, page: 1, total_count: 0, total_pages: 0 }))
      return
    }

    setBatch(batches.find(item => item?.batch_id === batchId) || null)
    setLoading(true)
    try {
      const response = await fetchData(
        'GET',
        showsGeneratedIssues
          ? buildGeneratedIssuesQuery(batchId, page, perPage, filterState)
          : showsClaims
          ? buildClaimsQuery(batchId, page, perPage, filterState)
          : buildVisitsQuery(batchId, page, perPage, filterState),
        {}
      )
      setRows(response?.data || [])
      if (showsGeneratedIssues) {
        const counts = response?.summary?.issue_counts || {}
        setIssueSummary(
          Object.entries(counts).map(([issue_type, issue_count]) => ({
            issue_type,
            issue_count,
            claims_count: issue_count
          }))
        )
      } else {
        setIssueSummary(showsClaims ? [] : response?.issue_summary || [])
      }
      const totalCount = response?.total_count ?? response?.total ?? 0
      const effectivePerPage = response?.per_page || response?.limit || perPage

      setPagination({
        page: response?.page || page,
        per_page: effectivePerPage,
        total_count: totalCount,
        total_pages: response?.total_pages || Math.ceil(totalCount / effectivePerPage)
      })
    } catch (error) {
      console.error('Error fetching batch details', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!shouldPersistFilters) return
    localStorage.setItem(
      `claimBatchStepFilters:${mode}`,
      JSON.stringify({
        month: selectedMonth,
        batch_id: filters.batch_id,
        issue_type: filters.issue_type,
        issue_status: filters.issue_status,
        file_number: filters.file_number,
        document_id: filters.document_id,
        patient_name: filters.patient_name,
        auto_fix_issue_types: autoFixIssueTypes
      })
    )
  }, [shouldPersistFilters, mode, selectedMonth, filters, autoFixIssueTypes])

  useEffect(() => {
    if (!shouldPersistFilters) return
    const pendingSnapshot = readStepReturnSnapshot(mode)
    if (pendingSnapshot && pendingSnapshot.mode === mode) {
      clearStepReturnSnapshot(mode)
      if (pendingSnapshot.showDetails) {
        const restoredBatchId = pendingSnapshot.batch_id
        const nextMonth = pendingSnapshot.month
        if (nextMonth && restoredBatchId) {
          const restoredFilters = {
            batch_id: restoredBatchId,
            issue_type: pendingSnapshot.filters?.issue_type || '',
            issue_status: pendingSnapshot.filters?.issue_status || '',
            file_number: pendingSnapshot.filters?.file_number || '',
            document_id: pendingSnapshot.filters?.document_id || '',
            patient_name: pendingSnapshot.filters?.patient_name || ''
          }
          const restoredPage = pendingSnapshot.pagination?.page || 1
          const restoredPerPage = pendingSnapshot.pagination?.per_page || 10
          const restoreView = async () => {
            setSelectedMonth(nextMonth)
            setFilters(restoredFilters)
            setSelectedBatchId(restoredBatchId)
            setPagination(prev => ({ ...prev, page: restoredPage, per_page: restoredPerPage }))
            setShowDetails(true)
            await fetchBatches(nextMonth)
          }
          restoreView()
          return
        }
      }
    }
    if (selectedMonth) {
      fetchBatches(selectedMonth)
      if (filters.batch_id) setSelectedBatchId(filters.batch_id)
    }
  }, [])

  useEffect(() => {
    if (!shouldPersistFilters) return
    saveStepReturnSnapshot(mode, {
      mode,
      showDetails,
      month: selectedMonth,
      batch_id: filters.batch_id || selectedBatchId || '',
      filters: {
        issue_type: filters.issue_type,
        issue_status: filters.issue_status,
        file_number: filters.file_number,
        document_id: filters.document_id,
        patient_name: filters.patient_name
      },
      pagination: {
        page: pagination.page,
        per_page: pagination.per_page
      }
    })
  }, [
    shouldPersistFilters,
    mode,
    showDetails,
    selectedMonth,
    filters,
    selectedBatchId,
    pagination.page,
    pagination.per_page
  ])

  useEffect(() => {
    if (!showDetails) return
    if (!selectedMonth) {
      setFilters(prev => ({ ...prev, batch_id: '' }))
      return
    }
    setFilters(prev => ({ ...prev, batch_id: selectedBatchId || '' }))
    fetchBatchDetails(selectedBatchId, pagination.page, pagination.per_page, {
      ...filters,
      batch_id: selectedBatchId || ''
    })
  }, [selectedBatchId, selectedMonth, showDetails, pagination.page, pagination.per_page])
  const handleApplyFilters = async () => {
    if (!selectedMonth) {
      toast.error(t('Please select a month'))
      return
    }
    if (!filters.batch_id) {
      toast.error(t('Please select a batch'))
      return
    }

    setSelectedBatchId(filters.batch_id)
    await fetchBatchDetails(filters.batch_id, 1, pagination.per_page, filters)
  }

  const handleResetFilters = () => {
    const resetFilters = {
      batch_id: '',
      issue_type: '',
      issue_status: '',
      file_number: '',
      document_id: '',
      patient_name: ''
    }
    setFilters(resetFilters)
    clearStepReturnSnapshot(mode)
    if (shouldPersistFilters) localStorage.removeItem("claimBatchStepFilters:" + mode)
    setSelectedMonth('')
    setBatches([])
    setSelectedBatchId('')
    setBatch(null)
    setRows([])
    setIssueSummary([])
    setAutoFixIssueTypes([])
    setPagination({
      page: 1,
      per_page: 10,
      total_count: 0,
      total_pages: 0
    })
  }

  // ----- audit row actions (only used in audit mode) -----

  // In audit mode, resolve full audit-result records (id / emr_id / issues) for the
  // batch's visits so the row actions (re-audit, auto-correct, delete, EMR) can run.
  useEffect(() => {
    if (mode !== 'audit') return
    const resolveAuditResults = async () => {
      const start = batch?.start_date
      const end = batch?.end_date
      if (!start || !end) return
      try {
        const payload = {
          page: 1,
          perPage: 100,
          start_date: start,
          end_date: end,
          audit_type: 'bulk',
          claim_type: '',
          payer_id: batch?.payers?.[0]?.payer_id || '',
          file_number: '',
          document_id: ''
        }
        const resp = await fetchData('POST', '/v3/invoices/audit-results/list', payload)
        const list = resp?.results || []
        if (list.length) {
          setRows(prev => {
            const byVisit = {}
            list.forEach(item => {
              byVisit[item.visit_id] = item
            })
            return prev.map(row => {
              const audit = byVisit[row.visit_id]
              if (!audit) return row
              return {
                ...row,
                audit_result_id: audit.id || row?.audit_result_id,
                emr_id: audit.emr_id || row?.emr_id,
                issues: audit.issues || row?.issues,
                audit_claim_id: audit.id || row?.audit_claim_id
              }
            })
          })
        }
      } catch (error) {
        console.error('Error resolving audit results for batch', error)
      }
    }
    resolveAuditResults()
  }, [mode, batch?.start_date, batch?.end_date, batch?.payers])

  const handleViewAuditDetail = row => {
    setSelectedAudit(row)
    setShowAuditDetailModal(true)
  }

  const handleReAuditClick = row => {
    setReauditVisitId(row?.visit_id)
    setShowReauditConfirm(true)
  }

  const handleConfirmReAudit = async () => {
    if (!reauditVisitId) return
    const row = rows.find(r => r?.visit_id === reauditVisitId)
    const fileNo = row?.file_number
    if (!fileNo) {
      toast.error(t('MRN not found for this visit'))
      setShowReauditConfirm(false)
      setReauditVisitId(null)
      return
    }
    setReauditLoading(true)
    try {
      const resp = await fetchData(
        'POST',
        `/v3/invoices/audit-results/${reauditVisitId}/re-audit?file_no=${encodeURIComponent(fileNo)}`,
        {}
      )
      if (resp?.success) {
        toast.success(resp.message || t('Re-audit completed successfully'))
        setShowReauditConfirm(false)
        setReauditVisitId(null)
        if (selectedBatchId) await fetchBatchDetails(selectedBatchId, pagination.page, pagination.per_page, filters)
      }
    } catch (error) {
      console.error('Error re-auditing visit', error)
      toast.error(error?.response?.data?.error || t('Re-audit failed'))
    } finally {
      setReauditLoading(false)
    }
  }

  const handleAutoCorrectClick = row => {
    setAutoCorrectTarget(row)
    setShowAutoCorrectConfirm(true)
  }

  const handleConfirmAutoCorrect = async () => {
    if (!autoCorrectTarget) return
    const visitId = autoCorrectTarget?.visit_id
    const auditClaimId = autoCorrectTarget?.audit_claim_id || autoCorrectTarget?.audit_result_id
    setCorrectingVisitId(visitId)
    try {
      const resp = await fetchData('POST', '/v3/invoices/audit/auto-correct/visit', {
        visit_id: visitId,
        audit_claim_id: auditClaimId
      })
      if (resp?.success) {
        toast.success(resp.message || t('Visit corrected successfully'))
        setShowAutoCorrectConfirm(false)
        setAutoCorrectTarget(null)
        if (selectedBatchId) await fetchBatchDetails(selectedBatchId, pagination.page, pagination.per_page, filters)
      }
    } catch (error) {
      console.error('Error auto-correcting visit', error)
      toast.error(error?.response?.data?.error || t('Auto-correct failed'))
    } finally {
      setCorrectingVisitId(null)
    }
  }

  const goToEmr = (row, createMode) => {
    const patientInfo = row?.patient_info || {}
    dispatch(
      setSelectPatientData({
        patient_id: row?.patient_id || patientInfo?.patient_id,
        file_no: row?.file_number || patientInfo?.file_number,
        document_id: row?.document_id || patientInfo?.document_id,
        full_name: row?.patient_name || patientInfo?.full_name
      })
    )
    dispatch(setSelectedVisitData({ visit_id: row?.visit_id }))

    const tabState = getTabState('claims_batch_flow_tabs')
    if (createMode) {
      navigateSecurely(navigate, '/patient-profile/create-emr', {
        visitData: { visit_id: row?.visit_id },
        returnTo: '/claims',
        previousState: tabState
      })
    } else {
      const emrId = row?.emr_id || row?.emr_numbers?.[0]
      navigateSecurely(navigate, '/patient-profile/edit-emr', {
        emr_id: emrId,
        visitData: { visit_id: row?.visit_id },
        returnTo: '/claims',
        previousState: tabState
      })
    }
  }

  const handleCreateEmrClick = row => goToEmr(row, true)
  const handleEditEmrClick = row => goToEmr(row, false)

  const handleViewBatch = async (row, month) => {
    if (!row?.batch_id) return

    const detailFilters = {
      batch_id: row.batch_id,
      issue_type: '',
      issue_status: '',
      file_number: '',
      document_id: '',
      patient_name: ''
    }

    setSelectedMonth(month || '')
    setFilters(detailFilters)
    setSelectedBatchId(row.batch_id)
    setShowDetails(true)
    await fetchBatches(month)
    await fetchBatchDetails(row.batch_id, 1, pagination.per_page, detailFilters)
  }
  const openJobProgressModal = row => {
    if (!row?.batch_id) return
    setJobBatch(row)
  }

  const handleMoveStep = async remarkText => {
    const targetBatch = moveBatch
    if (!targetBatch?.batch_id || !moveForm.target_step) {
      toast.error(t('Please select a step'))
      return
    }
    if (!remarkText?.trim()) {
      toast.error(t('Please enter a reason for the status change'))
      return
    }
    setMovingStep(true)
    try {
      await fetchData('POST', `v3/claims/batches/${targetBatch.batch_id}/move-step`, {
        target_step: moveForm.target_step,
        remarks: remarkText.trim()
      })
      toast.success(t('Batch step moved successfully'))
      setMoveBatch(null)
      setMoveForm({ target_step: '', remarks: '' })
      if (selectedMonth) await fetchBatches(selectedMonth)
      setListRefreshKey(key => key + 1)
      if (showDetails && targetBatch.batch_id === selectedBatchId) {
        await fetchBatchDetails(selectedBatchId, pagination.page, pagination.per_page, filters)
      }
    } catch (error) {
      console.error('Error moving batch step', error)
      toast.error(error?.response?.data?.error || t('Failed to move batch step'))
    } finally {
      setMovingStep(false)
    }
  }
  const openAutoFixModal = async (batchId, batchRow) => {
    if (!batchId) return
    setActionModalBatchId(batchId)
    setActionModalVisible(true)

    if (!issueSummary.length) {
      try {
        setAutoCorrectLoading(true)
        const params = new URLSearchParams({
          page: '1',
          per_page: '1'
        })
        const response = await fetchData('GET',
          isGeneratedAutoFixMode
            ? `v3/claims/batches/${batchId}/generated-issues?page=1&limit=1`
            : `v3/claims/batches/${batchId}/visits?${params.toString()}`,
          {})
        const generatedCounts = response?.summary?.issue_counts || {}
        const generatedSummary = Object.entries(generatedCounts).map(([issue_type, issue_count]) => ({
          issue_type,
          issue_count,
          claims_count: issue_count
        }))
        if (isGeneratedAutoFixMode && generatedSummary.length) {
          setIssueSummary(generatedSummary)
        } else if (response?.issue_summary?.length) {
          setIssueSummary(response.issue_summary)
        } else if (batchRow?.issue_summary?.length) {
          setIssueSummary(batchRow.issue_summary)
        }
      } catch (error) {
        console.error('Error fetching issue summary for auto-fix', error)
      } finally {
        setAutoCorrectLoading(false)
      }
    }
  }
  const getConfirmMessage = (stepMode, batchRow) => {
    const batchNumber = batchRow?.batch_number || getBatchNumber(batchRow?.batch_id) || '-'
    switch (stepMode) {
      case 'audit':
        return t('Are you sure you want to run audit for batch {{batchNumber}}?', { batchNumber })
      case 'generate':
        return t('Are you sure you want to generate claims for batch {{batchNumber}}?', { batchNumber })
      case 'generated-audit':
        return t('Are you sure you want to audit VAT, factor, and referral numbers in all generated claims for batch {{batchNumber}}?', { batchNumber })
      case 'submit':
        return t('Are you sure you want to submit claims for batch {{batchNumber}}? This will transmit all generated claims to the insurance payer.', { batchNumber })
      default:
        return t('Are you sure you want to proceed with batch {{batchNumber}}?', { batchNumber })
    }
  }

  const handleConfirmRunBatch = async row => {
    if (!row?.batch_id) return
    setRunningBatchId(row.batch_id)
    try {
      const completed = await handleAction(row.batch_id, '')
      if (completed) {
        setConfirmBatchRow(null)
        if (!showDetails) setListRefreshKey(key => key + 1)
      }
    } finally {
      setRunningBatchId('')
      setConfirmBatchRow(null)
    }
  }

  const runBatchFromList = row => {
    if (!row?.batch_id) return
    if (isAutoFixMode) {
      setIssueSummary([])
      setAutoFixIssueTypes([])
      openAutoFixModal(row.batch_id, row)
      return
    }
    if (needsRemarks(mode)) {
      setActionModalBatchId(row.batch_id)
      setOverrideUnresolved(false)
      setActionModalVisible(true)
      return
    }
    setConfirmBatchRow(row)
  }
  const handleAction = async (batchId = selectedBatchId, remarkText = '') => {
    if (!batchId) {
      toast.error(t('Please select a batch'))
      return false
    }
    if (needsRemarks(mode) && !remarkText?.trim()) {
      toast.error(t('Please enter a reason'))
      return false
    }

    setActionLoading(true)
    try {
      await fetchData(
        'POST',
        'v3/claims/batches/' + batchId + '/' + endpoints[mode],
        {
          remarks: needsRemarks(mode) ? remarkText.trim() : '',
          ...(mode === 'clearance' ? { override_unresolved: overrideUnresolved } : {}),
          ...(isAutoFixMode && autoFixIssueTypes.length
            ? { issue_types: autoFixIssueTypes }
            : {})
        }
      )
      toast.success(t('Batch step completed successfully'))
      if (selectedMonth) await fetchBatches(selectedMonth)
      if (showDetails && batchId === selectedBatchId) {
        await fetchBatchDetails(batchId, pagination.page, pagination.per_page, filters)
      }
      return true
    } catch (error) {
      console.error('Error running batch step', error)
      toast.error(error?.response?.data?.error || t('Action failed'))
      return false
    } finally {
      setActionLoading(false)
    }
  }
  const onPageChange = (page, perPage) => {
    setPagination(prev => ({ ...prev, page, per_page: perPage }))
  }

  const handleIssueFilterClick = async issueType => {
    const nextIssueType = filters.issue_type === issueType ? '' : issueType
    const nextFilters = {
      ...filters,
      issue_type: nextIssueType
    }
    setFilters(nextFilters)
    await fetchBatchDetails(selectedBatchId, 1, pagination.per_page, nextFilters)
  }

  const handleViewClaim = async row => {
    if (!row?.claim_id) return
    try {
      const [response, issueResponse] = await Promise.all([
        fetchData('POST', 'v3/invoices/listClaims', {
          page: 1,
          perPage: 1,
          claim_id: row.claim_id
        }),
        showsGeneratedIssues && selectedBatchId
          ? fetchData(
              'GET',
              `v3/claims/batches/${selectedBatchId}/generated-issues?page=1&limit=200&claim_id=${encodeURIComponent(row.claim_id)}`,
              {}
            )
          : Promise.resolve(null)
      ])
      const claimDetails = response?.data?.[0]
      if (!claimDetails) {
        toast.error(t('Claim details not found'))
        return
      }
      navigate('/claim-details', {
        state: {
          claimDetails,
          auditIssues: issueResponse?.data || [],
          auditSource: showsGeneratedIssues ? mode : ''
        }
      })
    } catch (error) {
      console.error('Error fetching claim details', error)
      toast.error(error?.response?.data?.error || t('Failed to load claim details'))
    }
  }
  const isClaimFailed = row => {
    const submissionStatus = String(row?.submission_status || '').toLowerCase()
    const claimStatus = String(row?.claim_status || '').toLowerCase()
    return submissionStatus === 'failed' || claimStatus === 'failed'
  }

  const handleResendClaim = async () => {
    const claimId = resendClaimTarget?.claim_id
    if (!claimId || resendingClaim) return

    setResendingClaim(true)
    try {
      // Resolve/refresh the RCM insurance plan first; only submit if it succeeds.
      try {
        await matchClaimInsurancePlan(claimId)
      } catch (matchError) {
        toast.error(
          matchError?.response?.data?.error ||
            t('Failed to match the claim insurance plan')
        )
        return
      }

      const response = await sendClaim(claimId)
      toast.success(response?.message || t('Claim submitted successfully'))
      await fetchBatchDetails(selectedBatchId)
    } catch (error) {
      if (error?.response) {
        toast.error(error?.response?.data?.error || t('Failed to send claim'))
      } else {
        toast.error(t('Submission status unknown. Refreshing claim status...'))
        await fetchBatchDetails(selectedBatchId)
      }
    } finally {
      setResendingClaim(false)
      setResendClaimTarget(null)
    }
  }

  const renderActionModals = () => (
    <>
      <ConfirmationModal
        visible={!!confirmBatchRow}
        setVisible={visible => {
          if (!visible && !actionLoading && !runningBatchId) setConfirmBatchRow(null)
        }}
        header={t(stepConfirmLabels[mode])}
        message={getConfirmMessage(mode, confirmBatchRow)}
        Icon={stepConfirmIcons[mode]}
        acceptLabel={t(stepConfirmLabels[mode])}
        rejectLabel={t('Cancel')}
        isLoading={actionLoading || runningBatchId === confirmBatchRow?.batch_id}
        position='top'
        onAccept={() => handleConfirmRunBatch(confirmBatchRow)}
        onReject={() => {
          if (!actionLoading && !runningBatchId) setConfirmBatchRow(null)
        }}
      />

      <RemarkModal
        isVisible={actionModalVisible}
        onClose={() => {
          if (actionLoading) return
          setActionModalVisible(false)
          setActionModalBatchId('')
          setOverrideUnresolved(false)
        }}
        title={t(stepConfirmLabels[mode])}
        HeaderIcon={stepConfirmIcons[mode]}
        confirmLabel={t(stepConfirmLabels[mode])}
        buttonType='success'
        confirmIcon={(() => {
          const Icon = stepConfirmIcons[mode]
          return <Icon size={15} />
        })()}
        onConfirm={remarkText => {
          const batchId = actionModalBatchId || selectedBatchId
          handleAction(batchId, remarkText).then(completed => {
            if (!completed) return
            setActionModalVisible(false)
            setActionModalBatchId('')
            setOverrideUnresolved(false)
            if (!showDetails) setListRefreshKey(key => key + 1)
          })
        }}
        remarkRequired={needsRemarks(mode)}
        showRemark={needsRemarks(mode)}
        loading={actionLoading || autoCorrectLoading}
        contextText={
          (() => {
            const batchNumber = getBatchNumber(actionModalBatchId || selectedBatchId)
            return batchNumber ? `${t('Batch No')}: ${batchNumber}` : ''
          })()
        }
      >
        {isAutoFixMode && (
          <div>
            <label className='input-label'>{t('Issue Types')}</label>
            <MultiSelectField
              useFormik={false}
              value={autoFixIssueTypes}
              handleChange={event => setAutoFixIssueTypes(event.value || [])}
              options={autoFixIssueTypeOptions}
              optionLabel='label'
              optionValue='value'
              placeholder={t('Select Issue Types')}
              filter
              filterPlaceholder={t('Search')}
              display='chip'
              className='!h-[36px]'
              showClear
            />
            {autoFixIssueTypes.length ? (
              <div className='mt-2 flex flex-wrap gap-1.5'>
                {autoFixIssueTypes.map(type => (
                  <span
                    key={type}
                    className='rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-800'
                  >
                    {formatWorkflowLabel(type)}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        )}
        {mode === 'clearance' && Number((batches.find(item => item?.batch_id === (actionModalBatchId || selectedBatchId)) || batch)?.generated_audit_summary?.unresolved_issues || 0) > 0 && (
          <label className='flex cursor-pointer items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
            <input
              type='checkbox'
              className='mt-0.5 h-4 w-4'
              checked={overrideUnresolved}
              onChange={event => setOverrideUnresolved(event.target.checked)}
            />
            <span>
              {t('Override unresolved generated-claim issues')} ({Number((batches.find(item => item?.batch_id === (actionModalBatchId || selectedBatchId)) || batch)?.generated_audit_summary?.unresolved_issues || 0)}). {t('A reason is required and will be recorded.')}
            </span>
          </label>
        )}
      </RemarkModal>

      <JobProgressModal
        isVisible={!!jobBatch}
        onClose={() => setJobBatch(null)}
        batchId={jobBatch?.batch_id}
        batchName={jobBatch?.batch_number || getBatchNumber(jobBatch?.batch_id)}
        stepTitle={mode ? t(stepTitles[mode]) : ''}
      />

      <ConfirmationModal
        visible={!!resendClaimTarget}
        setVisible={visible => {
          if (!visible) setResendClaimTarget(null)
        }}
        header={t('Send Again')}
        message={t(
          'Are you sure you want to send this claim again? A new submission attempt will be recorded.'
        )}
        Icon={IoIosSend}
        acceptLabel={t('Send Again')}
        rejectLabel={t('Cancel')}
        isLoading={resendingClaim}
        onAccept={handleResendClaim}
        onReject={() => setResendClaimTarget(null)}
      />

      {canMoveStep && (
        <RemarkModal
          isVisible={!!moveBatch}
          onClose={() => {
            if (movingStep) return
            setMoveBatch(null)
            setMoveForm({ target_step: '', remarks: '' })
          }}
          title={t('Move Batch Step')}
          HeaderIcon={MdOutlineMoveUp}
          confirmLabel={t('Move Step')}
          buttonType='success'
          onConfirm={handleMoveStep}
          loading={movingStep}
          contextText={moveBatch?.batch_number ? `${t('Batch No')}: ${moveBatch.batch_number}` : ''}
        >
          <div>
            <label className='input-label'>{t('Move To Step')}</label>
            <DropdownField
              useFormik={false}
              value={moveForm.target_step}
              handleChange={event =>
                setMoveForm(prev => ({ ...prev, target_step: event.target.value || '' }))
              }
              options={moveStepOptions}
              placeholder={t('Select Step')}
            />
          </div>
        </RemarkModal>
      )}

      {mode === 'audit' && (
        <>
          <ReusableModal
            isVisible={showAuditDetailModal}
            title={t('Claim Issues Details')}
            HeaderIcon={MdWarning}
            onClose={() => setShowAuditDetailModal(false)}
            className='w-full lg:w-[80%]'
            position='top'
          >
            {selectedAudit && (
              <div className='flex flex-col gap-3'>
                <div className='rounded border border-gray-200 bg-gray-50 p-3'>
                  <div className='grid grid-cols-1 gap-2 text-sm md:grid-cols-2'>
                    <div>
                      <span className='text-gray-600'>{t('Patient Name')}:</span>{' '}
                      <span className='font-semibold'>{selectedAudit?.patient_name || '-'}</span>
                    </div>
                    <div>
                      <span className='text-gray-600'>{t('MRN')}:</span>{' '}
                      <span className='font-semibold'>{selectedAudit?.file_number || '-'}</span>
                    </div>
                    <div>
                      <span className='text-gray-600'>{t('Document ID')}:</span>{' '}
                      <span className='font-semibold'>{selectedAudit?.document_id || '-'}</span>
                    </div>
                    <div>
                      <span className='text-gray-600'>{t('Visit ID')}:</span>{' '}
                      <span className='font-semibold'>{selectedAudit?.visit_id || '-'}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className='mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800'>
                    <span className='h-5 w-1 rounded bg-red-600' />
                    {t('Issues')} ({selectedAudit?.issues?.length || selectedAudit?.issue_types?.length || 0})
                  </h4>
                  {selectedAudit?.issues?.length > 0 ? (
                    <CustomTable
                      data={selectedAudit.issues}
                      columns={[
                        {
                          header: t('Issue Type'),
                          body: issue => (
                            <span className='rounded bg-red-50 px-2 py-1 font-medium uppercase text-red-600'>
                              {issue.issue_type || issue.type || '-'}
                            </span>
                          )
                        },
                        {
                          header: t('Item'),
                          body: issue =>
                            issue.item_name ? `${issue.item_name} - ${issue.item_code || ''}` : '—'
                        },
                        {
                          header: t('Description'),
                          body: issue => <p>{issue.description || '-'}</p>
                        }
                      ]}
                      emptyMessage={t('No issues found')}
                    />
                  ) : selectedAudit?.issue_types?.length > 0 ? (
                    <div className='flex flex-wrap gap-1.5'>
                      {selectedAudit.issue_types.map((issueType, idx) => (
                        <span
                          key={`${issueType}-${idx}`}
                          className='rounded bg-red-50 px-2 py-1 text-xs font-medium uppercase text-red-600'
                        >
                          {formatWorkflowLabel(issueType)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className='rounded border border-gray-200 py-8 text-center text-gray-500'>
                      {t('No issues found')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </ReusableModal>

          <ConfirmationModal
            visible={showReauditConfirm}
            setVisible={setShowReauditConfirm}
            header={t('Re-Audit Visit')}
            message={t('Are you sure you want to re-audit this visit? This will check for any new issues.')}
            Icon={MdRefresh}
            acceptLabel={t('Confirm')}
            rejectLabel={t('Cancel')}
            isLoading={reauditLoading}
            onAccept={handleConfirmReAudit}
            onReject={() => {
              setShowReauditConfirm(false)
              setReauditVisitId(null)
            }}
          />

          <ConfirmationModal
            visible={showAutoCorrectConfirm}
            setVisible={setShowAutoCorrectConfirm}
            header={t('Auto-Correct Visit')}
            message={t('Are you sure you want to auto-correct this visit? This will fix price/discount mismatches and follow-up consultation issues.')}
            Icon={MdBuild}
            acceptLabel={t('Confirm')}
            rejectLabel={t('Cancel')}
            isLoading={correctingVisitId !== null}
            onAccept={handleConfirmAutoCorrect}
            onReject={() => {
              setShowAutoCorrectConfirm(false)
              setAutoCorrectTarget(null)
            }}
          />

          <BulkSfdaAutoCorrectionModal
            isVisible={showSfdaModal}
            onClose={() => setShowSfdaModal(false)}
            onSuccess={() => { }}
          />

          <ICDBulkReplaceModal
            isVisible={showIcdModal}
            onClose={() => setShowIcdModal(false)}
          />
        </>
      )}
    </>
  )
  const visitColumns = [
    { header: t('Visit Date'), body: row => row?.visit_date || '-' },
    { header: t('Patient Name'), body: row => row?.patient_name || '-' },
    { header: t('MRN'), body: row => row?.file_number || '-' },
    { header: t('Document ID'), body: row => row?.document_id || '-' },
    { header: t('Visit ID'), body: row => row?.visit_id || '-' },
    {
      header: t('Payer'),
      body: row => row?.payer_name || '-',
      bodyCellClass: '!whitespace-normal !min-w-[180px]'
    },
    { header: t('Audit Status'), body: row => formatWorkflowLabel(row?.audit_status) || '-' },
    { header: t('Issues'), body: row => row?.issue_count || 0 },
    {
      header: t('Issue Types'),
      body: row => (row?.issue_types || []).map(formatWorkflowLabel).join(', ') || '-'
    }
  ]

  const auditActionColumn = {
    header: t('Actions'),
    body: row => {
      const hasAuditResult = !!row?.audit_result_id
      const issueTypes = (row?.issue_types || []).map(v => String(v || '').toLowerCase())
      const missingEmr =
        issueTypes.some(v => v.includes('missing') && v.includes('emr')) ||
        (row?.issues || []).some(issue =>
          String(issue?.issue_type || '').toLowerCase().includes('missing_emr')
        )
      const emrAvailable = !!row?.emr_id || !!row?.emr_numbers?.[0]?.length
      return (
        <div className='flex items-center gap-1.5'>
          <EyeIcon
            iconSize='17'
            title={t('View Audit Details')}
            onClick={() => handleViewAuditDetail(row)}
          />
          {missingEmr && !emrAvailable ? (
            <MdNoteAdd
              size={18}
              title={t('CreateEMR')}
              className='cursor-pointer text-primary'
              onClick={() => handleCreateEmrClick(row)}
            />
          ) : emrAvailable ? (
            <EditIcon
              title={t('Edit EMR')}
              onClick={() => handleEditEmrClick(row)}
            />
          ) : null}
          <MdRefresh
            size={17}
            title={t('Re-Audit')}
            className={
              reauditLoading && reauditVisitId === row?.visit_id
                ? 'animate-spin text-primary'
                : 'cursor-pointer text-gray-600 hover:text-primary'
            }
            onClick={() => !reauditLoading && handleReAuditClick(row)}
          />
          <MdBuild
            size={17}
            title={t('Auto-Correct Visit')}
            className={
              correctingVisitId === row?.visit_id
                ? 'animate-spin text-green-600'
                : 'cursor-pointer text-green-600'
            }
            onClick={() => !correctingVisitId && hasAuditResult && handleAutoCorrectClick(row)}
          />
        </div>
      )
    }
  }

  const generateVisitColumns = [
    ...visitColumns,
    {
      header: t('Claim Status'),
      body: row => formatWorkflowLabel(row?.claim_status) || '-'
    },
    {
      header: t('Claim Types'),
      body: row => (row?.claim_types || []).map(formatWorkflowLabel).join(', ') || '-'
    }
  ]
  const claimColumns = [
    {
      header: t('Actions'),
      body: row => (
        <div className='flex items-center gap-1.5'>
          <EyeIcon
            iconSize='17'
            title={t('View')}
            onClick={() => handleViewClaim(row)}
          />
          {mode === 'submit' && isClaimFailed(row) && !!row?.claim_id && (
            <IoIosSend
              size={17}
              title={t('Send Again')}
              className={
                resendingClaim
                  ? 'cursor-not-allowed text-gray-400'
                  : 'cursor-pointer text-primary'
              }
              onClick={() => !resendingClaim && setResendClaimTarget(row)}
            />
          )}
        </div>
      )
    },
    { header: t('Claim ID'), body: row => row?.claim_id || '-' },
    { header: t('Claim Type'), body: row => formatWorkflowLabel(row?.claim_type) || '-' },
    { header: t('Patient Name'), body: row => row?.patient_name || '-' },
    { header: t('MRN'), body: row => row?.file_number || '-' },
    { header: t('Document ID'), body: row => row?.document_id || '-' },
    {
      header: t('Payer'),
      body: row => row?.payer_name || '-',
      bodyCellClass: '!whitespace-normal !min-w-[180px]'
    },
    { header: t('Claim Status'), body: row => formatWorkflowLabel(row?.claim_status) || '-' },
    { header: t('Submission Status'), body: row => formatWorkflowLabel(row?.submission_status) || '-' },
    { header: t('Attempts'), body: row => row?.submit_attempt_count || 0 }
  ]
  const generatedIssueColumns = [
    {
      header: t('Actions'),
      body: row => (
        <EyeIcon
          iconSize='17'
          title={t('View Details')}
          onClick={() => handleViewClaim(row)}
        />
      )
    },
    { header: t('Claim ID'), body: row => row?.claim_id || '-' },
    { header: t('Visit ID'), body: row => row?.visit_id || '-' },
    { header: t('Item ID'), body: row => row?.item_id || '-' },
    { header: t('Issue Type'), body: row => formatWorkflowLabel(row?.issue_type) || '-' },
    { header: t('Actual Value'), body: row => formatIssueValue(row?.actual_value) },
    { header: t('Expected Value'), body: row => formatIssueValue(row?.expected_value) },
    { header: t('Corrected Value'), body: row => formatIssueValue(row?.corrected_value) },
    {
      header: t('Status'),
      body: row => (
        <CustomChip
          chipType={jobStatusChipType(row?.status)}
          label={formatWorkflowLabel(row?.status) || '-'}
          className='!h-[20px]'
          labelClassName='!text-[11px]'
        />
      )
    },
    {
      header: t('Failure Reason'),
      body: row => row?.failure_reason || '-',
      bodyCellClass: '!whitespace-normal !min-w-[220px]'
    }
  ]

  if (!showDetails) {
    return (
      <>
        <ClaimBatchStepList
          mode={mode}
          onView={handleViewBatch}
          onRunBatch={runBatchFromList}
          runningBatchId={runningBatchId}
          onShowJob={openJobProgressModal}
          workflowListMonth={workflowListMonth}
          refreshKey={listRefreshKey}
          extraActions={
            mode === 'audit' ? (
              <div className='flex items-center gap-2'>
                <CustomButton
                  label={t('Audit SFDA')}
                  buttonType='blue'
                  icon='pi pi-sync'
                  className='w-fit h-[35px]'
                  onClick={() => setShowSfdaModal(true)}
                />
                <CustomButton
                  label={t('Audit ICD')}
                  buttonType='secondary'
                  icon={FaExchangeAlt}
                  className='w-fit h-[35px]'
                  onClick={() => setShowIcdModal(true)}
                />
              </div>
            ) : null
          }
          onMoveStep={canMoveStep ? openMoveStepModal : null}
        />
        {renderActionModals()}
      </>
    )
  }

  return (
    <div>
      <div className='flex flex-wrap gap-2 mb-2'>
        <div className='w-[220px]'>
          <DropdownField
            useFormik={false}
            value={selectedMonth}
            handleChange={handleMonthChange}
            options={monthOptions}
            placeholder={t('Select Month')}
            filter
          />
        </div>
        <div className='w-[300px]'>
          <DropdownField
            useFormik={false}
            value={filters.batch_id}
            handleChange={event =>
              setFilters(prev => ({
                ...prev,
                batch_id: event.target.value
              }))
            }
            options={batchOptions}
            placeholder={t('Select Batch')}
            disabled={!selectedMonth}
            filter
          />
        </div>

        {!showsGeneratedIssues && <>
        <div className='w-[180px]'>
          <TextInput
            useFormik={false}
            name='file_number'
            value={filters.file_number}
            handleChange={event =>
              setFilters(prev => ({
                ...prev,
                file_number: event.target.value
              }))
            }
            placeholder={t('MRN')}
          />
        </div>

        <div className='w-[180px]'>
          <TextInput
            useFormik={false}
            name='document_id'
            value={filters.document_id}
            handleChange={event =>
              setFilters(prev => ({
                ...prev,
                document_id: event.target.value
              }))
            }
            placeholder={t('Document ID')}
          />
        </div>

        <div className='w-[220px]'>
          <TextInput
            useFormik={false}
            name='patient_name'
            value={filters.patient_name}
            handleChange={event =>
              setFilters(prev => ({
                ...prev,
                patient_name: event.target.value
              }))
            }
            placeholder={t('Patient Name')}
          />
        </div>
        </>}

        {showsGeneratedIssues && (
          <div className='w-[180px]'>
            <DropdownField
              useFormik={false}
              value={filters.issue_status}
              handleChange={event => setFilters(prev => ({ ...prev, issue_status: event.target.value || '' }))}
              options={[
                { value: 'pending', label: t('Pending') },
                { value: 'resolved', label: t('Resolved') },
                { value: 'failed', label: t('Failed') }
              ]}
              placeholder={t('Issue Status')}
              showClear
            />
          </div>
        )}

        <div className='flex gap-2 items-start flex-wrap'>
          <CustomButton
            title={t('ApplyFilters')}
            buttonType='primary'
            icon='pi pi-filter'
            className='w-fit h-[35px]'
            disabled={loading}
            onClick={handleApplyFilters}
          />
          <CustomButton
            title={t('Reset')}
            buttonType='reset'
            icon='pi pi-sync'
            className='w-fit h-[35px]'
            disabled={loading}
            onClick={handleResetFilters}
          />
          <CustomButton
            label={t('Batch List')}
            buttonType='reset'
            icon='pi pi-arrow-left'
            className='w-fit h-[35px]'
            onClick={() => {
              clearStepReturnSnapshot(mode)
              setShowDetails(false)
              setSelectedBatchId('')
            }}
          />
          {mode === 'audit' && (
            <>
              <CustomButton
                label={t('Audit SFDA')}
                buttonType='blue'
                icon='pi pi-sync'
                className='w-fit h-[35px]'
                onClick={() => setShowSfdaModal(true)}
              />
              <CustomButton
                label={t('Audit ICD')}
                buttonType='secondary'
                icon={FaExchangeAlt}
                className='w-fit h-[35px]'
                onClick={() => setShowIcdModal(true)}
              />
            </>
          )}
          {canMoveStep && (
            <CustomButton
              label={t('Move Step')}
              buttonType='warning'
              icon={<MdOutlineMoveUp size={16} />}
              className='w-fit h-[35px]'
              loading={movingStep}
              disabled={movingStep || actionLoading}
              onClick={() => openMoveStepModal(batch)}
            />
          )}
          <CustomButton
            label={t(stepTitles[mode])}
            buttonType='success'
            icon={stepIcons[mode]}
            className='w-fit h-[35px]'
            loading={actionLoading}
            disabled={
              actionLoading ||
              movingStep ||
              ['queued', 'processing'].includes(
                String(batch?.current_job?.status || '').toLowerCase()
              )
            }
            title={
              ['queued', 'processing'].includes(
                String(batch?.current_job?.status || '').toLowerCase()
              )
                ? t('A job is already running for this batch')
                : ''
            }
            onClick={() => {
              if (isAutoFixMode || needsRemarks(mode)) {
                setActionModalBatchId(selectedBatchId)
                setOverrideUnresolved(false)
                setActionModalVisible(true)
              } else {
                setConfirmBatchRow(batch)
              }
            }}
          />
        </div>
      </div>

      {!!batch?.current_job?.job_id && (
        <div className='mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2'>
          <div className='flex items-center gap-2 text-sm text-gray-600'>
            <span className='font-medium'>{t('Running Job')}:</span>{' '}
            <span>{formatWorkflowLabel(batch?.current_job?.job_type || '') || '-'}</span>
            <CustomChip
              chipType={jobStatusChipType(batch?.current_job?.status)}
              label={formatWorkflowLabel(batch?.current_job?.status) || '-'}
              className='!h-[20px] capitalize'
              labelClassName='!text-[11px]'
            />
          </div>
          <JobProgressIcon
            title={t('Current Job Progress')}
            onClick={() => setJobBatch(batch)}
          />
        </div>
      )}
      {!showsClaims && !!issueSummary.length && (
        <div className='mb-3 rounded-md border border-gray-200 bg-white px-3 py-2'>
          <div className='mb-2 text-sm font-medium text-gray-700'>{t('Issue Summary')}</div>
          <div className='flex max-h-[88px] flex-wrap gap-1.5 overflow-y-auto pr-1'>
            {issueSummary.map(item => {
              const isActive = filters.issue_type === item?.issue_type
              const label = formatWorkflowLabel(item?.issue_type) || '-'
              return (
                <button
                  key={item?.issue_type}
                  type='button'
                  title={item?.description || label}
                  onClick={() => handleIssueFilterClick(item?.issue_type || '')}
                  className={`inline-flex max-w-full items-center gap-1 rounded border px-2 py-1 text-xs transition-colors ${isActive
                    ? 'border-primary bg-blue-50 text-primary'
                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-primary/40'
                    }`}
                >
                  <span className='max-w-[230px] truncate font-semibold'>{label}</span>
                  <span className='text-gray-500'>{t('Issues')}: {item?.issue_count || 0}</span>
                  <span className='text-gray-500'>{t('Claims')}: {item?.claims_count || 0}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
      <CustomTable
        data={rows}
        columns={
          showsGeneratedIssues
            ? generatedIssueColumns
            : showsClaims
            ? claimColumns
            : (mode === 'audit' || mode === 'auto-fix')
              ? [auditActionColumn, ...visitColumns]
              : mode === 'generate'
                ? generateVisitColumns
                : visitColumns
        }
        isLoading={loading}
        emptyMessage={t('No records found')}
      />

      {!!pagination.total_count && (
        <CustomPagination
          currentPage={pagination.page}
          rowsPerPage={pagination.per_page}
          totalRecords={pagination.total_count}
          totalPages={pagination.total_pages || 1}
          onPageChange={onPageChange}
        />
      )}

      {renderActionModals()}
    </div>
  )
}

export default ClaimBatchStepTab
